import React, { useEffect, useState } from "react";
import ReactFlow from "reactflow";
import nodeTypes from "./NodeTypes";
import { initializeState, calculateIntermediateStates, completionLogicFC } from "utils/stateUtils";
import CloseIcon from "@mui/icons-material/Close";
import toast from "react-hot-toast";
const RunAndTest = ({ data, onClose, onApprove, onReject, onVerified }) => {
  const [switchStates, setSwitchStates] = useState({});
  const [isComplete, setIsComplete] = useState(false);
  const [activeEdges, setActiveEdges] = useState({});
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [completeNodeId, setCompleteNodeId] = useState("");

  // Define the level structure
  // const level = {
  //   nodes: [
  //     // Switch Nodes (Bottom Layer)
  //     {
  //       id: "1",
  //       type: "input",
  //       data: { label: "Board 1" },
  //       position: { x: 250, y: 500 },
  //     },
  //     {
  //       id: "2",
  //       type: "input",
  //       data: { label: "Board 2" },
  //       position: { x: 355, y: 500 },
  //     },
  //     // Gate Nodes (Middle Layer)
  //     {
  //       id: "3",
  //       type: "gate",
  //       data: {
  //         label: "OR",
  //         inputs: ["1", "2"], // Two input handles
  //         outputs: ["4"], // One output handle
  //       },
  //       position: { x: 295, y: 300 },
  //     },
  //     // Completion Light (Top Layer)
  //     {
  //       id: "4",
  //       type: "output",
  //       data: {
  //         label: "Completion Light",
  //         inputs: ["3"], // Two input handles
  //       },
  //       position: { x: 260, y: 150 },
  //     },
  //   ],
  //   edges: [
  //     // Connect Switches to Gates
  //     {
  //       id: "e1-3",
  //       source: "1",
  //       sourceHandle: "output",
  //       target: "3",
  //       targetHandle: "input-1",
  //       animated: true,
  //     },
  //     {
  //       id: "e2-3",
  //       source: "2",
  //       sourceHandle: "output",
  //       target: "3",
  //       targetHandle: "input-2",
  //       animated: true,
  //     },
  //     // Connect Gates to Completion Light
  //     {
  //       id: "e3-4",
  //       source: "3",
  //       sourceHandle: "output",
  //       targetHandle: "input-3",
  //       target: "4",
  //       animated: true,
  //     },
  //   ],
  //   completionLogic: (diodeButtonStates, activeEdges, edges) =>
  //     completionLogicFC(diodeButtonStates, activeEdges, edges, "4"),
  // };

  // const { nodes = [], edges = [], completionLogic } = level;

  useEffect(() => {
    if (!data) return;
    const n = data.nodes || data.levelData?.nodes || [];
    const e = data.edges || data.levelData?.edges || [];
    const outId = data.completeNodeId || data.outputId || "4";

    setNodes(n);
    setEdges(e);
    setCompleteNodeId(outId);

    const { initialSwitchStates, initialActiveEdges } = initializeState(n, e);
    calculateIntermediateStates(n, e, initialSwitchStates, initialActiveEdges);

    setSwitchStates(initialSwitchStates);
    setActiveEdges(initialActiveEdges);

    const result = completionLogicFC(initialSwitchStates, initialActiveEdges, e, outId);
    setIsComplete(result);
    if (result) {
      onVerified?.();
    }
  }, [data]);

  // Handle switch toggling
  const toggleSwitch = (id) => {
    setSwitchStates((prevSwitchStates) => {
      const newSwitchStates = { ...prevSwitchStates, [id]: !prevSwitchStates[id] };
      const newActiveEdges = {};

      calculateIntermediateStates(nodes, edges, newSwitchStates, newActiveEdges);
      setActiveEdges(newActiveEdges);

      const result = completionLogicFC(newSwitchStates, newActiveEdges, edges, completeNodeId);
      setIsComplete(result);

      if (result) {
        onVerified?.();
        toast.success("🎉 Level Solved & Circuit Verified Successfully!", {
          duration: 4000,
          style: {
            background: "#0f172a",
            color: "#34d399",
            border: "1px solid #10b981",
            boxShadow: "0 0 20px rgba(16, 185, 129, 0.4)",
            fontWeight: "bold",
            fontSize: "14px",
            borderRadius: "14px",
            padding: "14px 18px",
          },
        });
      }

      return newSwitchStates;
    });
  };

  // Log for debugging disappearing nodes
  console.log("Nodes in ReactFlow:", nodes);
  console.log("Edges in ReactFlow:", edges);

  return (
    <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-md flex justify-center items-center z-[999]">
      <div className="bg-slate-900 border border-cyan-500/40 relative p-6 rounded-2xl shadow-[0_0_40px_rgba(6,182,212,0.3)] text-white min-w-[520px] max-w-[600px]">
        {/* Modal Header Bar with Close Button */}
        <div className="flex justify-between items-center mb-4 pb-3 border-b border-slate-800">
          <h3 className="text-base font-bold text-cyan-400 flex items-center gap-2">
            <span>🎮</span> Circuit Logic Player & Simulator
          </h3>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-slate-800 hover:bg-rose-600 text-slate-300 hover:text-white border border-slate-700 flex items-center justify-center transition-all cursor-pointer"
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>

        <div className="flex gap-4">
          <div className="flex w-[450px] h-[480px] m-auto bg-slate-950 rounded-xl border border-slate-800 overflow-hidden">
            <ReactFlow
              nodes={nodes.map((node) => ({
                ...node,
                style: { background: "transparent", border: "none" },
                data: {
                  ...node.data,
                  isOn: node.type === "output" ? isComplete : switchStates[node.id],
                  toggleSwitch: () => toggleSwitch(node.id),
                },
              }))}
              edges={edges.map((edge) => ({
                ...edge,
                type: "smoothstep",
                animated: activeEdges[edge.id] || false,
                style: {
                  stroke: activeEdges[edge.id] ? "lime" : "gray",
                  strokeWidth: activeEdges[edge.id] ? 4 : 2,
                  filter: activeEdges[edge.id] ? "drop-shadow(0 0 6px lime)" : "none",
                },
              }))}
              nodesDraggable={false}
              nodesConnectable={false}
              nodeTypes={nodeTypes}
              fitView
              fitViewOptions={{ padding: 0.1 }}
              style={{ width: "100%", height: "100%", borderRadius: "10px" }}
              panOnDrag={false}
              panOnScroll={false}
              zoomOnScroll={false}
              zoomOnPinch={false}
              proOptions={{ hideAttribution: true }}
            />
          </div>
        </div>
        {(onApprove || onReject) && (
          <div className="flex items-center justify-end gap-3 mt-4 pt-3 border-t border-slate-800">
            {onReject && (
              <button
                onClick={onReject}
                className="px-5 py-2.5 bg-rose-600 hover:bg-rose-700 text-white font-bold text-xs rounded-xl shadow-[0_0_12px_rgba(244,63,94,0.4)] cursor-pointer"
              >
                Reject Level
              </button>
            )}
            {onApprove && (
              <button
                onClick={onApprove}
                className="px-5 py-2.5 bg-cyan-500 hover:bg-cyan-600 text-white font-bold text-xs rounded-xl shadow-[0_0_12px_rgba(6,182,212,0.4)] cursor-pointer"
              >
                Approve & Publish Level
              </button>
            )}
          </div>
        )}
      </div>
      <style>
        {`@keyframes pulse {
            0% { transform: scale(1); opacity: 1; }
            100% { transform: scale(1.5); opacity: 0; }
          }
          @keyframes zoomIn {
            0% { transform: scale(0.5); opacity: 0; }
            100% { transform: scale(1); opacity: 1; }
          }
          @keyframes explodeEffect {
            0% { opacity: 1; }
            100% { opacity: 0; }
          }`}
      </style>
    </div>
  );
};

export default RunAndTest;
